import { ApiEntity, ComponentEntity, RELATION_API_PROVIDED_BY, stringifyEntityRef } from "@backstage/catalog-model";
import { EmptyState } from "@backstage/core-components";
import { AsyncApiDefinitionWidget, OpenApiDefinitionWidget } from "@backstage/plugin-api-docs";
import { useEntity } from "@backstage/plugin-catalog-react";
import { AppEnvironment } from "@internal/open-catalog";
import { Button, Card, CardContent, createStyles, Divider, ListItem, ListItemIcon, ListItemText, makeStyles, MenuItem, TextField, Theme } from "@material-ui/core";
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import React, { ChangeEvent, useEffect, useState } from "react";
import { ButtonExportApi } from "./components/api.doc.exporter";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    divider: { marginTop: '1em' },
    header: {
      alignItems: 'center',
      padding: 16,
      paddingBottom: 0
    },
    headerForm: {
      alignItems: 'center',
      '& > *': {
        margin: theme.spacing(1),
        minWidth: 175,
        minHeight: 40
      }
    },
    fullscreen: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: 'calc(100vh - 32px)',
      width: 'calc(100vw - 32px)',
      margin: 16,
      zIndex: 10000
    },
    fullscreenContent: {
      overflowX: 'auto'
    },
  })
)


export const SystemApiPage = (props: { services: ComponentEntity[], apiDocs: ApiEntity[] }) => {

    const { services, apiDocs } = props;

    const classes = useStyles();

    const { entity } = useEntity<ComponentEntity>();

    // Safe initialization with fallback to prevent runtime errors
    const [service, setService] = useState<string>(services.length > 0 ? services[0].metadata.name : '');

    const [environment, setEnvironment] = useState<AppEnvironment>('production');

    const [specType, setSpecType] = useState<string>(apiDocs.map(a => a.spec.type).includes('openapi') ? 'openapi' : 'asyncapi');

    const [fullScreen, setFullScreen] = useState(false);

    const [apiDocsFiltered, setApiDocsFiltered] = useState([] as ApiEntity[]);

    const [apiDocsTypes, setApiDocsTypes] = useState(apiDocs);

    const handleServiceChange = (event: ChangeEvent<HTMLInputElement>) => {
        setService(event.target.value as any);
    };

    const handleEnvironmentChange = (event: ChangeEvent<HTMLInputElement>) => {
        setEnvironment(event.target.value as any);
    };

    const handleSpecTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSpecType(event.target.value as any);
    };

    const handleFullScreen = (_event: React.MouseEvent) => {
        setFullScreen(!fullScreen);
    };

    useEffect(() => {
        // Guard against invalid state: ensure entity, service are defined
        if (!entity || !service || !entity.metadata?.namespace) {
            setApiDocsFiltered([]);
            return;
        }
        
        setApiDocsFiltered(apiDocs.filter(api => api.spec.type === specType && api.spec.lifecycle === environment  && api.relations?.some(rel => rel.type === RELATION_API_PROVIDED_BY && rel.targetRef === stringifyEntityRef({ kind: 'Component', name: service, namespace: entity.metadata.namespace })) ) as ApiEntity[]);
    }, [service, environment, specType, apiDocs, entity]);

    useEffect(() => {
        // Guard against invalid state: ensure entity, service are defined
        if (!entity || !service || !entity.metadata?.namespace) {
            setApiDocsTypes([]);
            return;
        }
        
        setApiDocsTypes(apiDocs.filter(api => api.spec.lifecycle === environment  && api.relations?.some(rel => rel.type === RELATION_API_PROVIDED_BY && rel.targetRef === stringifyEntityRef({ kind: 'Component', name: service, namespace: entity.metadata.namespace })) ) as ApiEntity[]);
    }, [service, environment, apiDocs, entity]);
    return (<>

        <Card className={fullScreen ? classes.fullscreen : ''}>
              <ListItem className={classes.header}>
        
                <ListItemText>
                  <form noValidate autoComplete="off" className={classes.headerForm}>
                    <TextField select required
                      label="Service"
                      value={service}
                      onChange={handleServiceChange}
                      variant="outlined"
                      size="small"
                    >
                      {services?.map((svc) => (
                        <MenuItem key={svc.metadata.name} value={svc.metadata.name}>
                          {svc.metadata.name}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField select required
                        label="Environment"
                        value={environment}
                        onChange={handleEnvironmentChange}
                        variant="outlined"
                        size="small"
                    >
                        {['development', 'staging', 'production'].map((env) => (
                            <MenuItem key={env} value={env}>
                                {env}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField select required
                        label="Type"
                        value={specType}
                        onChange={handleSpecTypeChange}
                        variant="outlined"
                        size="small"
                    >
                        {/* Deduplicate by spec.type to avoid duplicate keys */}
                        {Array.from(new Set(apiDocsTypes.map(doc => doc.spec.type))).map((type) => {
                            const doc = apiDocsTypes.find(d => d.spec.type === type);
                            return (
                                <MenuItem key={type} value={type}>
                                    {doc?.metadata.description || type}
                                </MenuItem>
                            );
                        })}
                        {!apiDocsTypes.length && <MenuItem disabled key="none" value="none">
                            No APIs available
                        </MenuItem>}
                    </TextField>

                  </form>
                </ListItemText>
                {specType && apiDocsFiltered.map((api) => (
                <React.Fragment key={api.metadata.uid || `${api.metadata.namespace}-${api.metadata.name}`}>
                <ButtonExportApi apiDoc={api} />
                </React.Fragment>
                ))}
                <ListItemIcon>
                    <Button onClick={handleFullScreen} children={<AspectRatioIcon />} title="Toggle fullscreen" />
                </ListItemIcon>
              </ListItem>
              <Divider className={classes.divider} />

              <CardContent className={fullScreen ? classes.fullscreenContent : ''}>
                  {apiDocsFiltered.map((api) => (
                  <React.Fragment key={api.metadata.uid || `${api.metadata.namespace}-${api.metadata.name}`}>
                  {api.spec.type==='asyncapi' && <AsyncApiDefinitionWidget definition={api.spec.definition} />}
                  {api.spec.type==='openapi' && <OpenApiDefinitionWidget definition={api.spec.definition} />}
                  </React.Fragment>
                  ))}
                  { !apiDocsFiltered.length && (<EmptyState missing="content" title="No APIs available" />)}
              </CardContent>
        </Card>

    </>);
}